import React, { Component } from 'react';
import { PropTypes as PT } from 'prop-types';
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl';
import _get from 'lodash/get';
import _has from 'lodash/has';
import _last from 'lodash/last';
import _map from 'lodash/map';
import _throttle from 'lodash/throttle';
import MessageGroup from './ChatMessageGroup';
import classes from './ChatConversation.module.scss';

const messages = defineMessages({
  unknownUser: {
    id: 'unknownUser',
    defaultMessage: 'Unknown User'
  }
});

const conversationStarter = (conversation, isInCall, intl) => {
  const name = _get(conversation, 'contact.displayName') || _get(conversation, 'contact.name') || intl.formatMessage(messages.unknownUser);
  if (conversation && !conversation.isLoading) {
    if (isInCall) {
      return (
        <div className={`${classes.conversationStarter} ${classes.isInCall}`}>
          <FormattedMessage
            id="components.chat.conversation.newInCall"
            description="Prompt the user to start the in-call chat"
            defaultMessage="Be the first to chat!"
            values={{
              br: <br />,
              contactName: name
            }}
          />
        </div>
      );
    }
    return (
      <div className={classes.conversationStarter}>
        <FormattedMessage
          id="components.chat.conversation.new"
          description="Prompt the user to start the chat"
          defaultMessage="You haven’t chatted with {contactName} yet.{br}Type your first message below."
          values={{
            br: <br />,
            contactName: name
          }}
        />
      </div>
    );
  }
  return null;
};

const getLastMessage = (messageGroups) => {
  const lastMessageGroup = _last(messageGroups);
  const lastMessages = _get(lastMessageGroup, 'messages', []);

  return _last(lastMessages) || {};
};

const messageGroups = (conversation, isInCall) => _map(conversation.groupedMessages, group =>
  <MessageGroup
    key={`messageGroup-${group.timestamp.getTime()}`}
    group={group}
    isInCall={isInCall}
  />
);

class ChatConversation extends Component {
  constructor(props) {
    super(props);

    this.scrollHeightBeforeLoad = 0; // track to prevent scroll jumps when older messages are loaded
    this.conversationChangeInProgress = false; // switching from one conversation to another?
    this.resizeListener = _throttle(this.loadUnscrollableContent, 100);
  }

  componentDidMount() {
    // always scroll to bottom when first rendered
    this.conversationChangeInProgress = true;
    if (this.props.conversation && !this.props.conversation.isLoading) {
      this.setInitialScrollPosition(_get(this.props, 'conversation.conversationId'));
    } else if (!this.props.conversation) {
      // there is no conversation in search mode
      this.conversationChangeInProgress = false;
    }
    this.loadUnscrollableContent();
    // watch for scrolling
    this.conversationWindow.addEventListener('scroll', this.handleScroll);
    window.addEventListener('resize', this.resizeListener);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const prevConv = this.props.conversation || {};
    const newConv = nextProps.conversation || {};
    if (nextProps.hasNewChat) {
      this.conversationWindow.scrollTop = this.conversationWindow.scrollHeight;
      nextProps.resetChatFlag();
    }
    this.scrollToBottom = this.shouldScrollToBottom(
      _get(prevConv, 'groupedMessages'),
      _get(newConv, 'groupedMessages')
    );
    // bookmark the scroll position when changing conversations
    if (prevConv.conversationId !== newConv.conversationId) {
      this.bookmarkScroll({
        conversationId: prevConv.conversationId,
        lastMessageId: getLastMessage(_get(prevConv, 'groupedMessages', [])).archivedId
      });
    }
  }

  shouldComponentUpdate(nextProps) {
    if (nextProps.isGettingMessages) {
      return false;
    }
    return true;
  }

  componentDidUpdate(prevProps) {
    const prevConv = prevProps.conversation || {};
    const newConv = this.props.conversation || {};

    if (prevConv.conversationId !== newConv.conversationId) {
      this.conversationChangeInProgress = true;
    }
    if (this.conversationChangeInProgress) {
      // loading is finished following a change in conversation
      this.setInitialScrollPosition(newConv.conversationId);
    } else if (prevConv.isLoading && !newConv.isLoading) {
      this.conversationWindow.scrollTop
        = this.conversationWindow.scrollHeight - this.scrollHeightBeforeLoad;
    } else if (!this.conversationChangeInProgress && this.scrollToBottom) {
      // scroll immediately to the bottom
      this.conversationWindow.scrollTop = this.conversationWindow.scrollHeight;
    }

    this.loadUnscrollableContent();
  }

  componentWillUnmount() {
    // bookmark the scroll position when leaving chat
    this.bookmarkScroll({
      conversationId: _get(this.props, 'conversation.conversationId'),
      lastMessageId: getLastMessage(
        _get(this.props, 'conversation.groupedMessages', [])).archivedId
    });
    // remove event listener
    this.conversationWindow.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.resizeListener);
  }

  setInitialScrollPosition(conversationId) {
    const { isInCall, scrollBookmarks } = this.props;
    const lastMessageKey = isInCall ? 'lastMessageIdInCall' : 'lastMessageId';
    const scrollPositionKey = isInCall ? 'scrollPositionInCall' : 'scrollPosition';

    this.conversationChangeInProgress = false;
    if (conversationId
      && _has(scrollBookmarks, conversationId)
      && _has(scrollBookmarks[conversationId], lastMessageKey)
      && scrollBookmarks[conversationId][lastMessageKey] === getLastMessage(
        _get(this.props, 'conversation.groupedMessages', [])).archivedId) {
      /* if there have not been any new messages (lastMessageId of the bookmark matches current),
       * then scroll to the bookmarked position */
      this.conversationWindow.scrollTop = scrollBookmarks[conversationId][scrollPositionKey];
    } else {
      // otherwise (there have been new message(s), scroll to the bottom)
      this.conversationWindow.scrollTop = this.conversationWindow.scrollHeight;
    }
  }

  loadUnscrollableContent = () => {
    if (this.conversationWindow.scrollHeight <= this.conversationWindow.clientHeight &&
      !_get(this.props.conversation, 'isLoading') && _get(this.props.conversation, 'hasMoreMessages')) {
      this.loadMoreMessages();
    }
  };

  bookmarkScroll({
    conversationId,
    lastMessageId,
    scrollPosition = this.conversationWindow.scrollHeight
  }) {
    this.props.onBookmarkScroll({ conversationId, lastMessageId, scrollPosition });
  }

  loadMoreMessages = () => {
    this.scrollHeightBeforeLoad = this.conversationWindow.scrollHeight;
    this.props.onGetMoreMessages(this.props.conversation);
  };

  handleScroll = () => {
    if ((this.conversationWindow.scrollTop === 0)
      && !_get(this.props.conversation, 'isLoading') && _get(this.props.conversation, 'hasMoreMessages')) {
      // the user has scrolled to the top, so fetch new messages if they exist
      this.loadMoreMessages();
    } else if (_get(this.props.conversation, 'isLoading')) {
      /* more messages are still loading, so just update the bookmarked position until the new
       * messages arrive */
      this.scrollHeightBeforeLoad
        = this.conversationWindow.scrollHeight - this.conversationWindow.scrollTop;
    }
  };

  shouldScrollToBottom(oldMessageGroups, newMessageGroups) {
    const lastNewMessage = getLastMessage(newMessageGroups);
    const lastNewMessageIsMine = _get(lastNewMessage, 'isSenderSelf', false);
    const lastNewMessageId = _get(lastNewMessage, 'archivedId');
    const lastOldMessage = getLastMessage(oldMessageGroups);
    const lastOldMessageId = _get(lastOldMessage, 'archivedId');
    const lastMessageIsNew = lastNewMessageId && (lastNewMessageId !== lastOldMessageId);
    if (!this.conversationWindow) return false;
    const isScrolledToBottom
      = (this.conversationWindow.scrollHeight - this.conversationWindow.clientHeight)
      === this.conversationWindow.scrollTop;

    return lastMessageIsNew && (lastNewMessageIsMine || isScrolledToBottom);
  }

  render() {
    const { conversation, isInCall } = this.props;
    const groupedMessages = _get(conversation, 'groupedMessages.length', 0);

    return (
      <div
        className={classes.conversation}
        id="conversationWindow"
        ref={(elt) => { this.conversationWindow = elt; }}
      >
        {!_get(this.props.conversation, 'hasMoreMessages') && groupedMessages
          ? <div className={classes.beginningMarker}>
            <FormattedMessage
              id="components.chat.conversationBeginning"
              defaultMessage="Beginning of the conversation."
              description="Marks the beginning of a conversation"
            />
          </div>
          : null
        }
        {groupedMessages
          ? messageGroups(conversation, isInCall)
          : conversationStarter(conversation, isInCall, this.props.intl)
        }
      </div>
    );
  }
}

ChatConversation.propTypes = {
  conversation: PT.object,
  isGettingMessages: PT.bool,
  isInCall: PT.bool,
  onBookmarkScroll: PT.func,
  onGetMoreMessages: PT.func,
  scrollBookmarks: PT.object,
  hasNewChat: PT.bool,
  resetChatFlag: PT.func,
  intl: PT.object
};

ChatConversation.defaultProps = {
  conversation: { groupedMessages: [] },
  hasNewChat: false
};

export default injectIntl(ChatConversation);
